import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder,  Validators } from '@angular/forms';
import { UsuarioService } from 'src/app/services/usuario.service';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';
import * as bootstrap from 'bootstrap';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-my-account-identidad-comercio',
    templateUrl: './my-account-identidad-comercio.component.html',
    styleUrls: ['./my-account-identidad-comercio.component.css'],
    standalone: false
})
export class MyAccountIdentidadComercioComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _usuarioService = inject(UsuarioService);
  private _fb = inject(FormBuilder);
  private _location = inject(Location);
  private _loadingService = inject(LoadingService);
  private _authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  // ------- estado -------
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // máscara
  maskDocumento = signal<string>('00-00000000-0'); // CUIT/CUIL por defecto

  // combos
  tiposDocs = computed<TipoDocumento[]>(() => {
    const base = this._usuarioService.tiposDocumentos() ?? [];
    return [{ id: 0, x_descripcion: 'Seleccione', b_activo: true }, ...base];
  });

  // imágenes
  x_img_avatar?: File;
  preview_img_avatar = signal<string>('');
  preview_img_avatar_large = signal<string>('');
  porcentaje_comision_libresalud = signal<number>(0);

  private modalInstance?: any;

  // Form (tipado)
  readonly form = this._fb.nonNullable.group({
    razon_social: this._fb.nonNullable.control('', Validators.required),
    nombre_fantasia: this._fb.nonNullable.control('', Validators.required),
    tipo_documento: this._fb.nonNullable.control(0, Validators.required),
    nro_documento: this._fb.nonNullable.control('', [Validators.required, Validators.maxLength(13)]),
    nro_habilitacion: this._fb.nonNullable.control('', Validators.required),
    responsable_comercial: this._fb.nonNullable.control('', Validators.required),
    mercadopago: this._fb.nonNullable.control(true, Validators.required),
    envios: this._fb.nonNullable.control(true, Validators.required),
    vende_medicamentos: this._fb.nonNullable.control(true, Validators.required),
    porcentaje_descuento_medicamentos: this._fb.nonNullable.control<number | null>(null, [
      Validators.min(0),
      Validators.max(100),
      Validators.required,
    ]),
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // cerrar modal al volver atrás
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl && this.modalInstance) this.modalInstance.hide();
    });

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e) {
        this._router.navigate(['/sign-in']);
        return;
      }

      const comercio = this._usuarioService.comercio?.() as any;
      const usuario = this._usuarioService.usuario?.() as any;

      if (!comercio?.id || !usuario?.id) {
        this.error.set('No se pudo cargar tu cuenta. Volvé a intentar.');
        this._router.navigate(['/sign-in']);
        return;
      }

      // patch inicial
      // Importante: fijamos tipo_documento y la máscara ANTES de cargar
      // nro_documento. El cambio de [mask] recién llega al input en el
      // próximo ciclo de detección de cambios de Angular (no es sincrónico
      // con el .set() del signal) — por eso el patchValue de nro_documento
      // va en un setTimeout: si se hace en el mismo tick, ngx-mask todavía
      // tiene puesta la máscara vieja de DNI (8 dígitos) y trunca un
      // CUIT/CUIL (11 dígitos), aunque en la base el dato esté completo.
      this.form.patchValue({
        razon_social: comercio.x_razon_social?.trim() ?? '',
        nombre_fantasia: comercio.x_nombre_fantasia?.trim() ?? '',
        tipo_documento: usuario.id_tipo_documento ?? 0,
      });
      this.actualizarMascara();
      setTimeout(() => {
        this.form.patchValue({
          nro_documento: usuario.x_nro_documento?.trim() ?? '',
          nro_habilitacion: comercio.x_nro_habilitacion?.trim() ?? '',
          responsable_comercial: comercio.x_responsable_comercial?.trim() ?? '',
          mercadopago: comercio.b_mercadopago ?? true,
          envios: comercio.b_envios ?? true,
          vende_medicamentos: comercio.b_vende_medicamentos ?? true,
          porcentaje_descuento_medicamentos: comercio.n_porcentaje_descuento_medicamentos ?? null,
        });
      });

      // porcentaje comisión (solo lectura)
      this.porcentaje_comision_libresalud.set(usuario.n_porcentaje_comision ?? 0);

      // previews avatar
      if (usuario.x_img_avatar) {
        this.preview_img_avatar.set(`${API_URI}uploads/images/small-${usuario.x_img_avatar}`);
        this.preview_img_avatar_large.set(`${API_URI}uploads/images/large-${usuario.x_img_avatar}`);
      }
    });

    // si cambian el tipo doc, actualizar máscara
    this.form.controls.tipo_documento.valueChanges.subscribe(() => this.actualizarMascara());
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
  }

  back() {
    this._location.back();
  }

  // -------- Modal foto perfil --------
  openModal() {
    const modalEl = document.getElementById('modalPreviewImg');
    if (modalEl) {
      this.modalInstance = new (bootstrap as any).Modal(modalEl);
      this.modalInstance.show();
    }
  }

  // -------- Máscara --------
  actualizarMascara() {
    const tipo = this.form.getRawValue().tipo_documento;
    if (tipo === 1 || tipo === 2) { // 1 - CUIT / 2 - CUIL
      this.maskDocumento.set('00-00000000-0'); // CUIT / CUIL con formato 99-99999999-9
    } else {
      // DNI: permite 7 u 8 dígitos
      this.maskDocumento.set('00000009');
    }
  }

  // -------- Guardar --------
  async saveUsuario(): Promise<void> {
    this.error.set('');

    if (this.loading() || this.form.pending) return;

    const v = this.form.getRawValue();

    // Validaciones a mano (mismo comportamiento que el legacy)
    if (!v.nombre_fantasia) { this.error.set('Debe Completar el Nombre Fantasía'); return; }
    if (!v.razon_social) { this.error.set('Debe Completar la Razón Social'); return; }
    if (!v.nro_documento) { this.error.set('Debe Completar el Nro. de Documento'); return; }
    if ((v.nro_documento ?? '').length > 13) { this.error.set('El número de documento no puede tener más de 13 caracteres'); return; }
    if (!v.tipo_documento || v.tipo_documento === 0) { this.error.set('Debe Seleccionar el Tipo de Documento'); return; }
    if (!v.nro_habilitacion) { this.error.set('Debe Completar el Nro. de Habilitación'); return; }
    if (!v.responsable_comercial) { this.error.set('Debe Completar el Responsable Técnico/Comercial'); return; }
    if (v.porcentaje_descuento_medicamentos === null || v.porcentaje_descuento_medicamentos === ('' as any)) {
      this.error.set('Debe Completar el % de descuento');
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    await this._loadingService.run(async () => {
      try {
        this.form.disable({ emitEvent: false });

        // 1) actualizar comercio en el service (clon para reactividad)
        const comercio = { ...(this._usuarioService.comercio() as any) };
        comercio.x_nombre_fantasia = v.nombre_fantasia;
        comercio.x_razon_social = v.razon_social;
        comercio.x_nro_habilitacion = v.nro_habilitacion;
        comercio.x_responsable_comercial = v.responsable_comercial;
        comercio.b_mercadopago = v.mercadopago;
        comercio.b_envios = v.envios;
        comercio.b_vende_medicamentos = v.vende_medicamentos;
        comercio.n_porcentaje_descuento_medicamentos = v.porcentaje_descuento_medicamentos;
        this._usuarioService.comercio.set(comercio);

        const usuario = { ...(this._usuarioService.usuario() ?? {}) } as any;
        usuario.id_tipo_documento = v.tipo_documento!;
        usuario.x_nro_documento = v.nro_documento!;
        this._usuarioService.usuario.set(usuario);

        // 2) avatar (opcional)
        if (this.x_img_avatar) {
          const res: any = await firstValueFrom(
            this._usuarioService.updateUsuarioImgAvatar(this.x_img_avatar, this._usuarioService.usuario().x_email!)
          );

          const u = { ...this._usuarioService.usuario() };
          u.x_img_avatar = res?.imagen ?? u.x_img_avatar;
          this._usuarioService.usuario.set(u);
        }

        // 3) update comercio y usuario
        await firstValueFrom(this._usuarioService.updateComercio(this._usuarioService.comercio()));
        await firstValueFrom(this._usuarioService.updateUsuario(this._usuarioService.usuario()));

        // 4) Refrescamos si es socio o no
        await this._usuarioService.refreshAsociadoCardio();

        // 5) navegar
        await this._router.navigate(['/my-account']);
      } catch (e) {
        console.error(e);
        this.error.set('Error al actualizar el comercio');
      } finally {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  // -------- Imagen avatar (Capacitor Camera) --------
  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      resultType: CameraResultType.Uri,
      quality: 90,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  /** Convierte File a DataURL (para preview) */
  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  
  async capturarFileImgAvatar(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('avatar.jpg');
      this.x_img_avatar = file;
      this.preview_img_avatar.set(dataUrl);
      this.preview_img_avatar_large.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar avatar:', e);
    }
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })
}
